import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";

type Step = "credentials" | "twofactor";

interface SSOProvider {
  id: number;
  name: string;
  protocol: string;
  login_url: string;
}

export default function Login() {
  const { login, verifyTwoFactor, acceptSsoToken, user } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("credentials");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [partialToken, setPartialToken] = useState("");
  const [code, setCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [providers, setProviders] = useState<SSOProvider[]>([]);
  // On-prem AD sign-in is a direct credential POST (no redirect), so
  // clicking an AD provider reveals an inline username/password/domain form
  // rather than navigating away.
  const [adProvider, setAdProvider] = useState<SSOProvider | null>(null);
  const [adUsername, setAdUsername] = useState("");
  const [adPassword, setAdPassword] = useState("");
  const [adDomain, setAdDomain] = useState("");
  const [signoutNotice, setSignoutNotice] = useState("");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  // If the API 401'd us out because the account signed in elsewhere
  // (single-session enforcement), explain the sign-out once. The flag is set
  // by the axios interceptor and consumed here so it doesn't linger.
  useEffect(() => {
    try {
      const reason = sessionStorage.getItem("signout_reason");
      if (reason === "session_superseded") {
        setSignoutNotice(
          "You were signed out because your account was used to sign in on another device. Only one session per user is allowed.",
        );
      } else if (reason === "session_idle_timeout") {
        setSignoutNotice(
          "You were signed out after a period of inactivity. Sign in again to continue.",
        );
      } else if (reason === "session_revoked") {
        // Distinct from the other two on purpose. Telling someone their
        // account was used on another device when an administrator had simply
        // signed them out invites a security panic over a routine action.
        setSignoutNotice(
          "An administrator signed you out. Sign in again to continue.",
        );
      }
      if (reason) sessionStorage.removeItem("signout_reason");
    } catch {
      /* sessionStorage unavailable — non-fatal */
    }
  }, []);

  // Public endpoint — no auth required. If it fails we just hide the
  // SSO buttons; password login still works.
  useEffect(() => {
    api
      .get("/api/v1/sso/providers")
      .then((r) => setProviders(r.data || []))
      .catch(() => setProviders([]));
  }, []);

  // If the callback bounced us back here with ?sso_error=..., surface it
  // before the user retries. (The dedicated /sso/return page handles
  // the success path; this is just defense in depth.)
  useEffect(() => {
    const e = searchParams.get("sso_error");
    if (e) setError(e);
  }, [searchParams]);

  const startSso = (p: SSOProvider) => {
    setError("");
    // On-prem AD/LDAP is a direct bind we perform via XHR — reveal the
    // inline credential form instead of redirecting.
    if (p.protocol === "ad") {
      setAdProvider(p);
      setAdUsername("");
      setAdPassword("");
      setAdDomain("");
      return;
    }
    // OIDC/SAML: top-level navigation — must NOT be an XHR. The backend
    // 302s to the IdP, which redirects back to /api/v1/sso/callback with
    // a state cookie; XHR would not follow cross-origin or set cookies
    // correctly.
    const returnTo = encodeURIComponent("/");
    window.location.href = `${p.login_url}?return_to=${returnTo}`;
  };

  const handleAdLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adProvider) return;
    setError("");
    setLoading(true);
    try {
      const r = await api.post("/api/v1/sso/ldap/login", {
        provider_id: adProvider.id,
        username: adUsername.trim(),
        password: adPassword,
        domain: adDomain.trim() || null,
      });
      await acceptSsoToken(r.data.token);
      navigate("/");
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (!err.response) setError("Cannot reach server. Is the backend running?");
        else if (err.response.status === 401)
          setError(
            err.response.data?.detail || "Active Directory rejected those credentials.",
          );
        else if (err.response.status === 500)
          setError(
            err.response.data?.detail ||
              "The directory could not be reached. Ask an administrator to check the AD configuration.",
          );
        else setError(`Sign in failed (${err.response.status}).`);
      } else {
        setError("Unexpected error during sign in.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(username, password);
      if (result.requires2fa) {
        const factors = result.availableFactors || [];
        if (factors.length === 0) {
          // Server demands 2FA but the account has no usable second factor —
          // don't strand the user on a 2FA screen with no path forward.
          setError(
            "Two-factor authentication is required but no second factor is enrolled on this account. Ask an administrator to reset your MFA.",
          );
          return;
        }
        setPartialToken(result.partialToken);
        setStep("twofactor");
        setCode("");
        setUseBackupCode(false);
      } else {
        navigate("/");
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (!err.response) setError("Cannot reach server. Is the backend running?");
        else if (err.response.status === 401) setError("Invalid username or password");
        else
          setError(
            `Server error (${err.response.status}). Try again or check the server logs.`,
          );
      } else {
        setError("Unexpected error during sign in.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactor = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verifyTwoFactor(partialToken, code.trim());
      navigate("/");
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError(
          useBackupCode
            ? "That backup code isn't valid (or was already used)."
            : "Invalid code. Check your authenticator app and try again.",
        );
      } else {
        setError("Verification failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Whether to show the brand band at all. A deployment with no logo uploaded,
  // or one whose logo 404s, falls back to the plain accent strip rather than a
  // dark band with a hole in it.
  const showLogo = Boolean(branding.has_logo && branding.logo_url && !logoFailed);

  const cancelTwoFactor = () => {
    setStep("credentials");
    setPartialToken("");
    setCode("");
    setError("");
    setUseBackupCode(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="h-1.5 w-full bg-brand" />

        <div className="p-8">
          <div className="text-center mb-7">
            {showLogo && (
              /* The mark, at a size that reads as the product rather than as a
                 favicon — it was 44px high before. Rounded with a soft shadow
                 so a square logo on a solid background presents as an app icon;
                 nothing is drawn behind it, because anything peeking out from
                 under the artwork reads as a stray stripe rather than as
                 branding. */
              <img
                src={branding.logo_url ?? undefined}
                alt={branding.company_name}
                onError={() => setLogoFailed(true)}
                className="h-28 w-auto max-w-[15rem] mx-auto mb-5 object-contain rounded-2xl shadow-lg"
              />
            )}
            <div className="text-[11px] uppercase tracking-[0.15em] text-gray-500 dark:text-gray-400 font-semibold mb-1">
              {branding.console_eyebrow}
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Sign in to continue</h1>
          </div>

          {signoutNotice && (
            <p className="text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-3 py-2 rounded-lg mb-5">
              {signoutNotice}
            </p>
          )}

          {step === "credentials" && adProvider && (
            <form onSubmit={handleAdLogin} className="space-y-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Sign in with your <span className="font-medium">{adProvider.name}</span> (Active
                Directory) account.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Username</label>
                <input
                  type="text"
                  value={adUsername}
                  onChange={(e) => setAdUsername(e.target.value)}
                  required
                  autoFocus
                  autoComplete="username"
                  placeholder="jdoe"
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Password</label>
                <input
                  type="password"
                  value={adPassword}
                  onChange={(e) => setAdPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Domain <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={adDomain}
                  onChange={(e) => setAdDomain(e.target.value)}
                  placeholder="corp.local"
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>
              {error && (
                <p className="text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading || !adUsername || !adPassword}
                className="w-full bg-brand text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-brand-dark transition-colors disabled:opacity-50 shadow-sm"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setAdProvider(null);
                  setError("");
                }}
                className="w-full text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                ← Back to other sign-in options
              </button>
            </form>
          )}

          {step === "credentials" && !adProvider && (
            <form onSubmit={handleCredentials} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Username</label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>
              {error && (
                <p className="text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-brand-dark transition-colors disabled:opacity-50 shadow-sm"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
              <div className="text-center">
                <a
                  href="/forgot-password"
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-brand dark:hover:text-brand-light transition-colors"
                >
                  Forgot password?
                </a>
              </div>

              {providers.length > 0 && (
                <div className="pt-3">
                  <div className="relative my-2">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                    </div>
                    <div className="relative flex justify-center text-[11px] uppercase tracking-wider">
                      <span className="bg-white dark:bg-gray-800 px-3 text-gray-400 dark:text-gray-500">or</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {providers.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => startSso(p)}
                        className="w-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                      >
                        Sign in with {p.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </form>
          )}

          {step === "twofactor" && (
            <form onSubmit={handleTwoFactor} className="space-y-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {useBackupCode
                  ? "Enter one of your single-use backup codes."
                  : "Open your authenticator app and enter the 6-digit code to continue."}
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  {useBackupCode ? "Backup code" : "Authentication code"}
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  autoFocus
                  autoComplete="one-time-code"
                  inputMode={useBackupCode ? "text" : "numeric"}
                  placeholder={useBackupCode ? "ABCD-1234-WXYZ" : "123456"}
                  maxLength={useBackupCode ? 16 : 6}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-lg font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>
              {error && (
                <p className="text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading || !code}
                className="w-full bg-brand text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-brand-dark transition-colors disabled:opacity-50 shadow-sm"
              >
                {loading ? "Verifying…" : "Verify"}
              </button>
              <div className="flex flex-col gap-1.5 text-xs pt-1">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setUseBackupCode(!useBackupCode);
                      setCode("");
                      setError("");
                    }}
                    className="text-brand dark:text-brand-light hover:underline font-medium"
                  >
                    {useBackupCode ? "Use authenticator code instead" : "Use a backup code instead"}
                  </button>
                  <button
                    type="button"
                    onClick={cancelTwoFactor}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  >
                    Back
                  </button>
                </div>
              </div>
            </form>
          )}

          <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-7 pt-5 border-t border-gray-100 dark:border-gray-700">
            Need help? Contact{" "}
            <a
              href={`mailto:${branding.support_email}`}
              className="text-brand dark:text-brand-light font-medium hover:underline"
            >
              {branding.support_email}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
